// apps/crm-frontend/src/pages/ReportPage.tsx
// 報表頁面 - 客戶來源、標籤分布、每月新增客戶、每月營收

import React from 'react';
import { ProportionList, MonthlyBarChart } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import { useReportStore, reportKey } from '../stores/reportStore';
import { normalizeSource } from '../components/forms';
import { toIntlLocale } from '@redpanda/utils';

// 圖表卡片：標題＋右側摘要＋內容（§3-1 區塊標題 text-base font-semibold）
const ReportCard: React.FC<{ title: string; aside?: React.ReactNode; className?: string; children: React.ReactNode }> = ({
  title, aside, className = '', children,
}) => (
  <section className={`bg-surface rounded-lg border border-line p-5 ${className}`}>
    <div className="mb-4 flex items-baseline justify-between gap-3">
      <h2 className="text-base font-semibold text-fg">{title}</h2>
      {aside && <div className="text-[13px] text-fg-secondary tabular-nums">{aside}</div>}
    </div>
    {children}
  </section>
);

const ReportPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const locale = toIntlLocale(i18n.language);

  // 先顯示上次的資料（localStorage），每次進頁面或切換「含封存」時背景重抓；沒有快取時才顯示載入中
  const report = useReportStore(state => state.reports[reportKey(includeArchived)]);
  const fetchReports = useReportStore(state => state.fetchReports);
  const [isFetching, setIsFetching] = React.useState(true);
  React.useEffect(() => {
    setIsFetching(true);
    fetchReports(includeArchived).finally(() => setIsFetching(false));
  }, [includeArchived, fetchReports]);
  const isLoading = !report && isFetching;

  // 「2026-10」→ 同年「10月」、跨年「2025年12月」（§3-2 同年日期不帶年份）
  const formatMonth = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return month;
    const sameYear = y === new Date().getFullYear();
    return new Date(y, m - 1, 1).toLocaleDateString(locale, sameYear ? { month: 'short' } : { year: 'numeric', month: 'short' });
  };
  const formatCount = (v: number) => v.toLocaleString(locale);
  const formatMoney = (v: number) => `$${v.toLocaleString(locale)}`;
  const formatMoneyAxis = (v: number) => `$${new Intl.NumberFormat(locale, { notation: 'compact' }).format(v)}`;

  // 來源代碼（OTHER、SOCIAL_MEDIA）→ 翻譯 key（other、socialMedia）
  const sourceLabel = (source: string) => t(`common.forms.sources.${normalizeSource(source)}`, { defaultValue: source });

  const loadingText = <p className="py-8 text-center text-sm text-fg-secondary">{t('common.status.loading')}</p>;
  const emptyText = (text: string) => <p className="py-8 text-center text-sm text-fg-secondary">{text}</p>;

  // 後端以原始 source 字串分組；舊資料可能同時有 OTHER 與 other（2026-10 起後端寫入一律存大寫代碼），
  // 前端依正規化後的值合併計數
  const sources: Array<{ source: string; count: number }> = Object.values(
    (report?.sources || []).reduce((acc, s) => {
      const key = normalizeSource(s.source);
      acc[key] = { source: key, count: (acc[key]?.count || 0) + s.count };
      return acc;
    }, {} as Record<string, { source: string; count: number }>)
  );
  const tags = (report?.tags || []).filter(tag => tag.count > 0);
  const trend = report?.trend || [];
  const revenue = report?.revenue || [];
  const totalCustomers = trend.length > 0 ? trend[trend.length - 1].cumulative : 0;

  return (
    <div className="container mx-auto px-10 py-6 pb-32">
      <div className="mb-6">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{t('reports.title', { ns: 'pages' })}</h1>
          <label htmlFor="includeArchived" className="flex items-center gap-2 text-sm text-fg-secondary cursor-pointer">
            <input
              type="checkbox"
              id="includeArchived"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-slate-300 dark:border-slate-600 text-brand-600 focus:ring-brand-500"
            />
            {t('reports.includeArchivedCustomers', { ns: 'pages' })}
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* 客戶來源：比例條；只有一個分類時直接寫文字（§3-6 不畫 100% 圓餅） */}
        <ReportCard title={t('reports.customerSource', { ns: 'pages' })}>
          {isLoading ? loadingText
            : sources.length === 0 ? emptyText(t('reports.charts.noData', { ns: 'pages' }))
            : sources.length === 1 ? (
              <p className="text-sm text-fg">
                {t('reports.singleSource', { ns: 'pages', count: sources[0].count, source: sourceLabel(sources[0].source) })}
              </p>
            ) : (
              <ProportionList
                otherLabel={sourceLabel('other')}
                otherKey="other"
                items={sources.map(s => ({ key: s.source, label: sourceLabel(s.source), count: s.count }))}
              />
            )}
        </ReportCard>

        {/* 標籤分布：比例條，標籤顏色只用在名稱前的小點 */}
        {/* 一位客戶可有多個標籤：百分比的分母是標籤使用次數，不是客戶數，在標題旁註明 */}
        <ReportCard title={t('reports.tagDistribution', { ns: 'pages' })} aside={t('reports.tagUsageBasis', { ns: 'pages' })}>
          {isLoading ? loadingText
            : tags.length === 0 ? emptyText(t('reports.noTagUsage', { ns: 'pages' }))
            : (
              <ProportionList
                otherLabel={t('reports.otherTags', { ns: 'pages' })}
                items={tags.map(tag => ({ key: tag.tag, label: tag.tag, count: tag.count, color: tag.color }))}
              />
            )}
        </ReportCard>

        {/* 每月新增客戶（原本「客戶增長趨勢」與「月度客戶增長」是同一份資料畫兩次，合併） */}
        <ReportCard
          title={t('reports.monthlyCustomerGrowth', { ns: 'pages' })}
          aside={!isLoading && trend.length > 0 && `${t('reports.totalCustomers', { ns: 'pages' })} ${formatCount(totalCustomers)}`}
        >
          {isLoading ? loadingText
            : trend.length === 0 ? emptyText(t('reports.charts.noTrendData', { ns: 'pages' }))
            : (
              <MonthlyBarChart
                data={trend.map(d => ({ month: d.month, value: d.count }))}
                formatValue={formatCount}
                formatMonth={formatMonth}
              />
            )}
        </ReportCard>

        {/* 每月營收：時間序列用長條圖（§3-6），Y 軸精簡格式避免數字被截斷 */}
        <ReportCard title={t('reports.monthlyRevenue', { ns: 'pages' })}>
          {isLoading ? loadingText
            : revenue.length === 0 ? emptyText(t('reports.charts.noData', { ns: 'pages' }))
            : (
              <MonthlyBarChart
                data={revenue.map(d => ({ month: d.month, value: d.total }))}
                formatValue={formatMoney}
                formatAxis={formatMoneyAxis}
                formatMonth={formatMonth}
              />
            )}
        </ReportCard>
      </div>
    </div>
  );
};

export default ReportPage;
